// WebSocket client for the Warbirds.io protocol: JSON control messages plus
// binary snapshots (see internal/game/binsnap.go / binsnap.js).

import { createSnapshotDecoder, decodeKillcam, KILLCAM_MAGIC } from "./binsnap.js";
import { wsURL } from "./server.js";

export class Net {
  constructor() {
    this.ws = null;
    this.joined = false;
    this.spectator = false;
    this.onWelcome = null;
    this.onSnapshot = null;
    this.onBoard = null;
    this.onEvents = null;
    this.onKillcam = null;
    this.onClose = null;
    this._pingTimer = null;
    // Row-recycling decoder for the live stream. Every snapshot here is
    // handed straight to the renderer and consumed before the next arrives,
    // so its rows can be reused instead of freshly minted 20 times a second.
    // Killcam clips go through the plain decoder — they are RETAINED and
    // replayed frame by frame, so they need rows of their own.
    this._decodeSnapshot = createSnapshotDecoder();
  }

  connect() {
    const url = wsURL();
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      this.ws = ws;
      ws.onopen = () => {
        // Keep the server's read deadline fed while sitting on the menu or
        // spectating (players feed it with their 30 Hz inputs instead).
        this._pingTimer = setInterval(() => {
          if (!this.joined || this.spectator) this.send({ t: "ping" });
        }, 10000);
        resolve();
      };
      ws.onerror = () => reject(new Error("websocket error"));
      ws.onclose = () => {
        clearInterval(this._pingTimer);
        this.onClose?.();
      };
      ws.onmessage = (e) => {
        if (e.data instanceof ArrayBuffer) {
          if (!this.joined || e.data.byteLength === 0) return;
          if (new DataView(e.data).getUint8(0) === KILLCAM_MAGIC) {
            try {
              this.onKillcam?.(decodeKillcam(e.data));
            } catch (err) {
              console.error("killcam decode failed", err);
            }
            return;
          }
          let snap;
          try {
            snap = this._decodeSnapshot(e.data);
          } catch (err) {
            console.error("snapshot decode failed", err);
            return;
          }
          this.onSnapshot?.(snap);
          return;
        }
        let msg;
        try {
          msg = JSON.parse(e.data);
        } catch {
          return;
        }
        switch (msg.t) {
          case "welcome":
            this.joined = true;
            this.onWelcome?.(msg);
            break;
          case "ev":
            if (this.joined) this.onEvents?.(msg.ev ?? []);
            break;
          case "board":
            this.onBoard?.(msg);
            break;
        }
      };
    });
  }

  send(obj) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
    }
  }

  // gunKind: 0 = fly the chosen plane; 1/2/3 man a FLAK / NAVY / BIG GUNS
  // mount at the first spawn (wire value is the AA site kind + 1). veh: drive
  // a ground vehicle of type veh-1 instead (0 = none; wins over gunKind).
  // tur: crew a bomber turret seat tur-1 instead (0 = none; wins over both),
  // on airframe type tp-1 (tp 0 = any bomber with that seat free).
  // train: a non-empty scenario id drops into a private single-player drill
  // (the server seats/positions the player itself).
  // ff: ask for the guided first sortie (firstflight.js). Only ever a REQUEST —
  // the server grants it solely to a join token whose career is genuinely
  // empty, so a lost localStorage flag can't farm the gift kills.
  // ins: the hangar's unit insignia pick — null (never picked on this device)
  // leaves the career's saved badge alone; "" clears it.
  join(name, plane, token, gunKind = 0, veh = 0, train = "", tur = 0, scheme = 0, sid = "", ff = false, ins = null, tp = 0) {
    const msg = { t: "join", name, plane, tok: token, gk: gunKind, veh, tur };
    if (tp) msg.tp = tp; // the airframe that seat rides: type + 1 (seat numbers are per airframe)
    if (train) msg.train = train;
    if (ff) msg.ff = true;
    if (scheme) msg.skn = scheme; // equipped paint scheme (roadmap #25)
    if (sid) msg.sid = sid; // per-load analytics session id (analytics.go)
    if (ins != null) msg.ins = ins; // unit insignia (insignia.js)
    this.send(msg);
  }

  // Leave the guided first sortie for the shared world. The graduation and the
  // SKIP TO BATTLE escape are the same message: the server swaps this
  // connection's world and sends a second welcome down the same socket — no
  // reconnect, no navigation, no menu bounce.
  graduateFirstFlight() {
    this.send({ t: "ffgo" });
  }

  spectate() {
    this.spectator = true;
    this.send({ t: "spectate" });
  }

  setPlane(plane) {
    this.send({ t: "plane", plane });
  }

  /** Harmonize one airframe's guns at meters (0 = back to the factory boresight). */
  setConvergence(plane, meters) {
    this.send({ t: "conv", plane, cv: meters });
  }

  setScheme(scheme) {
    this.send({ t: "scheme", skn: scheme }); // repaint (roadmap #25)
  }

  setInsignia(ins) {
    this.send({ t: "insignia", ins: ins || "" }); // swap the unit badge now
  }

  /** Press TAKE OFF while down: respawn now (the server holds it to RespawnAt). */
  respawn() {
    this.send({ t: "respawn" });
  }

  /** Skip the death replay: drop the killcam's respawn hold to the plain floor. */
  skipcam() {
    this.send({ t: "skipcam" });
  }

  /** Reserve an AA gun slot for the next spawn (0 = fly a plane / bail out). */
  setGun(gun) {
    this.send({ t: "gun", gun });
  }

  /** Drive a ground vehicle of type veh-1 at the next spawn (0 = fly / bail out). */
  setVehicle(veh) {
    this.send({ t: "veh", veh });
  }

  /** Crew a bomber turret seat tur-1 on airframe tp-1 at the next spawn (tur 0 = fly / bail out; tp 0 = any bomber). */
  setTurret(tur, tp = 0) {
    this.send(tp ? { t: "tur", tur, tp } : { t: "tur", tur });
  }

  /** Bail out of a flying airframe: pop the canopy, abandon the plane. */
  bail() {
    this.send({ t: "bail" });
  }

  /** Launch from the carrier deck at the next spawn (sticky preference). */
  setDeck(deck) {
    this.send({ t: "deck", deck });
  }

  /** Battleship main battery: designate the bombardment coordinate. */
  plot(x, z) {
    this.send({ t: "plot", x: Math.round(x), z: Math.round(z) });
  }

  /** Pilot calls a fire mission at their current position. */
  spot() {
    this.send({ t: "spot" });
  }

  comms(idx) {
    this.send({ t: "comms", plane: idx });
  }

  setHeading(hdg) {
    this.send({ t: "hdg", hd: Math.round(hdg * 1000) / 1000 });
  }

  chat(text) {
    this.send({ t: "chat", text });
  }

  sendInput(ctl, fire, bomb, seq) {
    const m = {
      t: "i",
      seq,
      p: round3(ctl.pitch),
      r: round3(ctl.roll),
      y: round3(ctl.yaw),
      th: round3(ctl.throttle),
      f: fire,
      b: bomb,
    };
    // Naval: loaded shell (0 HE / 1 AP / 2 SS) and the damage-control request ride along.
    if (ctl.ammo) m.am = ctl.ammo;
    if (ctl.dc) m.dc = true;
    if (ctl.smoke) m.sm = true;
    if (ctl.repair) m.rp = true;
    if (ctl.boost) m.bo = true;
    if (ctl.aimRange) m.ar = Math.round(ctl.aimRange); // naval: range (m) under the gunsight crosshair (auto-elevation)
    // Combined arms: that point's height (m), to a decimetre. The shallow naval
    // arcs are this sensitive: a metre off moves the fall of shot 20-50 m.
    if (ctl.aimHeight) m.ah = Math.round(ctl.aimHeight * 10) / 10;
    // Jets era: the missile launch and flare requests (rising edges server-side).
    if (ctl.msl) m.ml = true;
    if (ctl.flare) m.fl = true;
    this.send(m);
  }
}

const round3 = (v) => Math.round(v * 1000) / 1000;
